import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import apiClient from "@/lib/apiClient";
import { Helmet } from 'react-helmet-async';
import { useAuth } from "@/components/AuthContext";
import Header from "../../header";
import { toast } from "sonner";
import { ThemedButton, ThemedCard, ThemedInput } from "@/components/themed";
import {
  clearInitialAdvertisingAttribution,
  getInitialAdvertisingAttribution,
} from "@/utils/advertisingAttribution";

interface Site {
  id: number;
  name: string;
  subdomain: string;
  slug: string;
  description: string;
  icon_url: string | null;
  thumbnail_url: string | null;
  privacy_url: string | null;
  terms_url: string | null;
  trade_law_url: string | null;
  created_at: string;
  identity_verified: boolean;
  antique_dealer_verified: boolean;
  delivery_count: number;
  referral_conversion_point?: 'registration' | 'phone_verified' | 'first_purchase';
  new_customer_registration_enabled?: boolean;
}

interface SignupPageProps {
  siteId: number;
  siteData?: Site;
}

const SignupPage: React.FC<SignupPageProps> = ({ siteId, siteData }) => {
  const { currentUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [passwordStrength, setPasswordStrength] = useState<"weak" | "medium" | "strong" | "">("");
  const registrationEnabled = siteData?.new_customer_registration_enabled !== false;

  // URLパラメータから招待コードを取得
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) {
      setReferralCode(refCode);
      setShowReferralInput(true);
    }
  }, []);

  // ログイン済みユーザーのリダイレクト
  useEffect(() => {
    if (currentUser) {
      window.location.href = "/";
    }
  }, [currentUser]);

  const evaluatePasswordStrength = (password: string) => {
    if (password.length < 8) {
      setPasswordStrength("weak");
      return;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const strength =
      (hasUpperCase ? 1 : 0) +
      (hasLowerCase ? 1 : 0) +
      (hasNumbers ? 1 : 0) +
      (hasSpecialChars ? 1 : 0);

    if (strength <= 2) {
      setPasswordStrength("weak");
    } else if (strength === 3) {
      setPasswordStrength("medium");
    } else {
      setPasswordStrength("strong");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    try {
      const advertisingAttribution = getInitialAdvertisingAttribution();
      const response = await apiClient.post("/customer/registrations", {
        email,
        password,
        site_id: siteId,
        referral_code: referralCode || undefined,
        advertising_attribution: advertisingAttribution,
      });

      // メールアドレスを localStorage に保存（認証待ち画面で使用）
      localStorage.setItem("pendingVerificationEmail", email);
      clearInitialAdvertisingAttribution();

      // 即座にメール認証待ち画面へ遷移
      window.location.href = "/email-verification-pending";

    } catch (err: any) {
      setError(err.response?.data?.errors?.join(", ") || "エラーが発生しました");
    }
  };

  return (
    <>
      <Helmet>
        <title>会員登録 | {siteData?.name || 'ショップ'}</title>
        <meta property="og:title" content={`会員登録 | ${siteData?.name || 'ショップ'}`} />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <Header siteData={siteData} />

        <section className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 pt-24">
          <div className="w-full max-w-md space-y-6">
            {!registrationEnabled ? (
              <ThemedCard className="p-8 text-center">
                <h2 className="text-2xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">新規会員登録を停止しています</h2>
                <p className="mt-3 text-sm text-gray-600">現在、このサイトでは新しいアカウントを登録できません。</p>
                <ThemedButton type="button" variant="primary" className="mt-6 w-full" onClick={() => { window.location.href = "/login"; }}>
                  ログイン画面へ
                </ThemedButton>
              </ThemedCard>
            ) : (
            <>
            <div className="text-center">
              <h2 className="text-3xl [font-weight:var(--theme-font-weight)] tracking-tight text-theme-bg-text">
                会員登録
              </h2>
              <p className="mt-2 text-sm [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">
                {siteData?.name}への会員登録
              </p>
            </div>

            <ThemedCard className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-100 border-2 border-red-500 rounded-theme p-4">
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-red-700">{error}</p>
                  </div>
                )}
                {successMessage && (
                  <div className="bg-green-100 border-2 border-green-500 rounded-theme p-4">
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-green-700">{successMessage}</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="email" className="block text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1">
                    メールアドレス
                  </Label>
                  <ThemedInput
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@example.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="block text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-1">
                    パスワード
                  </Label>
                  <ThemedInput
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      evaluatePasswordStrength(e.target.value);
                    }}
                    placeholder="********"
                    required
                  />
                  {password && (
                    <div className="mt-3">
                      <div className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-700 mb-1">パスワード強度:
                        <span className={
                          passwordStrength === "weak" ? "text-red-600 ml-1 [font-weight:var(--theme-font-weight)]" :
                          passwordStrength === "medium" ? "text-yellow-600 ml-1 [font-weight:var(--theme-font-weight)]" :
                          "text-green-600 ml-1 [font-weight:var(--theme-font-weight)]"
                        }>
                          {passwordStrength === "weak" ? "弱い" :
                           passwordStrength === "medium" ? "普通" : "強い"}
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-200 rounded-full border-theme overflow-hidden">
                        <div
                          className={
                            passwordStrength === "weak" ? "h-full bg-red-500 w-1/3 transition-all" :
                            passwordStrength === "medium" ? "h-full bg-yellow-500 w-2/3 transition-all" :
                            "h-full bg-green-500 w-full transition-all"
                          }
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* 招待コード入力 */}
                <div>
                  {!showReferralInput ? (
                    <button
                      type="button"
                      onClick={() => setShowReferralInput(true)}
                      className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 hover:text-black underline underline-offset-2"
                    >
                      招待コードをお持ちの方
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="referralCode" className="block text-sm [font-weight:var(--theme-font-weight-button)] text-black">
                        招待コード
                      </Label>
                      <ThemedInput
                        id="referralCode"
                        type="text"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        placeholder="例: ABC12345"
                        maxLength={8}
                      />
                      <p className="text-xs text-gray-500">
                        {`友達から共有された招待コードを入力すると、${
                          siteData?.referral_conversion_point === 'registration' ? 'メール認証完了時' :
                          siteData?.referral_conversion_point === 'first_purchase' ? '初回購入時' :
                          '電話番号認証完了時'
                        }にポイントがもらえます`}
                      </p>
                    </div>
                  )}
                </div>

                <ThemedButton
                  type="submit"
                  variant="primary"
                  className="w-full py-3"
                >
                  会員登録
                </ThemedButton>

                <div className="mt-6 text-center text-sm text-gray-700">
                  <p className="[font-weight:var(--theme-font-weight-button)]">
                    登録することで、
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="[font-weight:var(--theme-font-weight)] text-black hover:text-theme-primary underline underline-offset-2 mx-1"
                    >利用規約</a>
                    および
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="[font-weight:var(--theme-font-weight)] text-black hover:text-theme-primary underline underline-offset-2 mx-1"
                    >プライバシーポリシー</a>
                    に同意したものとみなします。
                  </p>
                </div>
              </form>

              <div className="mt-6 text-center">
                <span className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mr-1">既にアカウントをお持ちですか？</span>
                <a
                  href="/login"
                  className="text-sm [font-weight:var(--theme-font-weight)] text-black hover:text-theme-primary underline underline-offset-2"
                >
                  ログイン
                </a>
              </div>
            </ThemedCard>
            </>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default SignupPage;
